import { useTranslation } from 'react-i18next';
import {
  IpponButton,
  IpponDropdown,
  IpponHSpace,
  IpponIcon,
  IpponSeparator,
  IpponText,
  IpponVSpace,
} from '@ippon-ui/react';
import { LoaderComponent, LoadingSkeleton } from '@/generic/loader';
import { useOptional } from '@/generic/react-tools';
import { logout } from '@/generic/authentication';
import type { Organization, User } from '@/supporting/account';
import type { CurrentUser, CurrentUserLoader } from '@/cleanapi/CurrentUser.ts';
import type { Optional } from '@/generic/functional';
import type { ToggleEventHandler } from 'react';

const USER_MENU_ID = 'cleanapi-user-menu';

const OrganizationComponent = ({ organization }: { organization: Optional<Organization> }) =>
  useOptional({
    optional: organization,
    onValuated: (organization) => (
      <IpponHSpace gap={4} align="middle">
        <IpponIcon name="business" size={12} color="neutral-tertiary" />
        <IpponText
          variant="label"
          size="small"
          color="neutral-tertiary"
          dataSelector="cleanapi.user-menu.organization"
        >
          {organization.name}
        </IpponText>
      </IpponHSpace>
    ),
  });

const ProfileComponent = ({ user }: { user: User }) => (
  <>
    <IpponHSpace gap={8} align="middle">
      <IpponIcon name="person-circle" size={40} color="neutral-tertiary" />
      <IpponVSpace gap={2}>
        <IpponText variant="body" weight="bold" dataSelector="cleanapi.user-menu.name">
          {user.displayName}
        </IpponText>
        <OrganizationComponent organization={user.organization} />
      </IpponVSpace>
    </IpponHSpace>
    <IpponSeparator />
  </>
);

const ProfileLoadingComponent = () => (
  <>
    <LoadingSkeleton>
      <IpponHSpace gap={8} align="middle">
        <IpponIcon name="person-circle" size={40} color="neutral-tertiary" />
        <IpponVSpace gap={2}>
          <IpponText variant="body" weight="bold" placeholder />
          <IpponText variant="label" size="small" color="neutral-tertiary" placeholder />
        </IpponVSpace>
      </IpponHSpace>
    </LoadingSkeleton>
    <IpponSeparator />
  </>
);

const ProfileErrorComponent = ({ error }: { error: Error }) => {
  const { t } = useTranslation('cleanapi');

  return (
    <>
      <IpponText
        variant="label"
        size="small"
        color="error-primary"
        dataSelector="cleanapi.user-menu.error"
      >
        {t('cleanapi.user-menu.error')}: {error.message}
      </IpponText>
      <IpponSeparator />
    </>
  );
};

const onCurrentUserLoading = () => <ProfileLoadingComponent />;

const onCurrentUserError = (error: Error) => <ProfileErrorComponent error={error} />;

const onCurrentUser = (currentUser: CurrentUser) =>
  currentUser.fold({
    error: () => <></>,
    success: (user) => <ProfileComponent user={user} />,
  });

export const UserMenuComponent = ({
  currentUser,
  onRefreshCurrentUser,
}: {
  currentUser: CurrentUserLoader;
  onRefreshCurrentUser: () => void;
}) => {
  const { t } = useTranslation('cleanapi');

  const refreshUser: ToggleEventHandler<HTMLDivElement> = (event) => {
    if (event.newState === 'open') {
      onRefreshCurrentUser();
    }
  };

  return (
    <>
      <IpponButton
        size="large"
        variant="text"
        iconLeft={{ name: 'person-circle' }}
        popoverTarget={USER_MENU_ID}
        dataSelector="cleanapi.user-menu"
      />
      <IpponDropdown
        id={USER_MENU_ID}
        onToggle={refreshUser}
        dataSelector="cleanapi.user-menu.dropdown"
      >
        <LoaderComponent
          loader={currentUser}
          onLoading={onCurrentUserLoading}
          onError={onCurrentUserError}
          onSuccess={onCurrentUser}
        />
        <IpponButton
          color="error"
          variant="text"
          iconLeft={{ name: 'log-out' }}
          onClick={logout}
          dataSelector="cleanapi.user-menu.log-out"
        >
          {t('cleanapi.user-menu.log-out')}
        </IpponButton>
      </IpponDropdown>
    </>
  );
};
